import { MsalProvider } from "@azure/msal-react";
import { CssBaseline } from "@mui/material";
import { LicenseInfo } from "@mui/x-license-pro";
import { NotificationDefault } from "components";
import { MUILicenseKey, QueryClientGlobal, MSALGlobal } from "configs";
import {
  AppNavigationProvider,
  DialogServiceProvider,
  UseAlert,
  UseConfirm,
  alert,
} from "contexts";
import AppThemeProvider from "contexts/Theme/provider";
import { AppInitializer, AzureInitializer } from "libs";
import { SnackbarProvider } from "notistack";
import { ErrorBoundary } from "react-error-boundary";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { Provider as ReduxProvider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";
import { AppRoutes } from "routes";
import store, { persistor } from "stores";
import { i18n } from "translations";

import "./index.scss";

if (process.env.NODE_ENV === "production" && MUILicenseKey) {
  LicenseInfo.setLicenseKey(MUILicenseKey);
}

const onErrorHandler = () => {
  alert({
    title: i18n.t("oopsSomethingWentWrong"),
    content: i18n.t("pleaseReloadAndTryAgain"),
    cancelLabel: i18n.t("reload"),
    disableCloseOnBackdropAndEscape: true,
  })
    .then(() => window.location.reload())
    .catch(() => {});
};

function App() {
  return (
    <QueryClientProvider client={QueryClientGlobal as QueryClient}>
      <ReduxProvider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <MsalProvider instance={MSALGlobal}>
            <AppThemeProvider showModeTool={false}>
              <BrowserRouter>
                <AppNavigationProvider>
                  <DialogServiceProvider>
                    <ErrorBoundary
                      fallbackRender={() => null}
                      onError={onErrorHandler}
                    >
                      <SnackbarProvider
                        maxSnack={3}
                        autoHideDuration={3000}
                        Components={{
                          default: NotificationDefault,
                          success: NotificationDefault,
                          error: NotificationDefault,
                          warning: NotificationDefault,
                          info: NotificationDefault,
                        }}
                      >
                        <CssBaseline />
                        <ReactQueryDevtools />
                        <AppInitializer />
                        <AzureInitializer />
                        <UseConfirm />
                        <UseAlert />
                        <AppRoutes />
                      </SnackbarProvider>
                    </ErrorBoundary>
                  </DialogServiceProvider>
                </AppNavigationProvider>
              </BrowserRouter>
            </AppThemeProvider>
          </MsalProvider>
        </PersistGate>
      </ReduxProvider>
    </QueryClientProvider>
  );
}

export default App;
